import httpStore, {
  getSelectedStoreURL,
  getSelectedDepartmentCode
} from "@/api/httpStore.js";

export function createQueryString(data) {
  const params = [];
  function flatten(obj) {
    Object.keys(obj).forEach(key => {
      const val = obj[key];
      if (val !== null && val !== undefined && typeof val === "object") {
        flatten(val);
      } else {
        params.push(
          `${key}=${encodeURIComponent(`${val}`.replace(/\s/g, "_"))}`
        );
      }
    });
  }
  flatten(data);
  return params.join("&");
}

const ordersCache = new Map();

// Payment types rarely change: one request per store per session
const paymentTypesCache = new Map();

// Infoauto payment types, the list every store used before the server sent
// its own. Used while a store's API has no /orders/payment_types yet.
const INFOAUTO_PAYMENT_TYPES = [
  { code: "EFE", name: "Efectivo" },
  { code: "CHQ", name: "Cheque" },
  { code: "TCR", name: "Tarjeta de Crédito" },
  { code: "TDB", name: "Tarjeta de Débito" },
  { code: "NDB", name: "Nota de Débito Bancaria" },
  { code: "NCRBAN", name: "Nota de Crédito Bancaria - ZELLE" },
  { code: "DEPBAN", name: "Depósito Bancario" },
  { code: "CASHEA", name: "Cashea" },
  { code: "SINPE", name: "SINPE Movil" },
  { code: "PLATD", name: "Plataforma Digital" }
];

/**
 * Payment types the active store offers in an order ({code, name, isDefault,
 * isCreditDefault}), in display order. Each store has its own list; stores
 * whose API does not serve it yet get the Infoauto list.
 * @param {string} storeCode - cache key, the active store's code
 */
export function getOrderPaymentTypes(storeCode) {
  if (!paymentTypesCache.has(storeCode)) {
    const request = httpStore
      .get("/orders/payment_types")
      .then(res => res.data)
      .catch(e => {
        // REST_Controller answers 405 for a method the controller lacks
        if ([404, 405].includes(e.response?.status)) {
          return INFOAUTO_PAYMENT_TYPES;
        }
        // Retry on the next call instead of caching a transient error
        paymentTypesCache.delete(storeCode);
        throw e;
      });
    paymentTypesCache.set(storeCode, request);
  }
  return paymentTypesCache.get(storeCode);
}
const ORDERS_CACHE_TTL_MS = 15000;
// La misma búsqueda da otras órdenes en otra tienda o departamento (van en la
// URL y el header de httpStore, no en los filtros).
const ordersCacheScope = () =>
  `${getSelectedStoreURL()}|${getSelectedDepartmentCode()}|`;

// Original, fully-joined endpoint (orders_v2 + address + customers + sellers + ...).
// Kept as-is on purpose so /orders and /orders2 can be compared side by side.
export function getOrders(filters, { skipCache = false } = {}) {
  const newFilters = createQueryString(filters);
  const cacheKey = ordersCacheScope() + newFilters;
  const cached = ordersCache.get(cacheKey);
  if (
    !skipCache &&
    cached &&
    Date.now() - cached.timestamp < ORDERS_CACHE_TTL_MS
  ) {
    return Promise.resolve(cached.data);
  }
  return httpStore.get(`/orders/set?${newFilters}`).then(res => {
    ordersCache.set(cacheKey, { data: res.data, timestamp: Date.now() });
    return res.data;
  });
}

// Fast list endpoint backed only by orders_v2.search_index / list_metadata
// (see Orders::list_get / Order_model::getOrdersFromIndex in usa-admin-store-api).
// Supports every OrdersFilters field plus search, statusShipment, orderCol,
// orderDir, page and limitPerPage. Used by OrdersPage2 (served at /orders).
export function getOrdersFromIndex(filters, { skipCache = false } = {}) {
  const newFilters = createQueryString(filters);
  const cacheKey = `index:${ordersCacheScope()}${newFilters}`;
  const cached = ordersCache.get(cacheKey);
  if (
    !skipCache &&
    cached &&
    Date.now() - cached.timestamp < ORDERS_CACHE_TTL_MS
  ) {
    return Promise.resolve(cached.data);
  }
  return httpStore.get(`/orders/list?${newFilters}`).then(res => {
    ordersCache.set(cacheKey, { data: res.data, timestamp: Date.now() });
    return res.data;
  });
}

// CSV con todas las ordenes que cumplen los filtros de /orders/list (sin
// paginar). Pide el header de auth, asi que se baja como blob y se dispara la
// descarga a mano, igual que downloadBulkRowsCsv.
export function exportOrdersCsv(filters) {
  return httpStore
    .get(`/orders/export_csv?${createQueryString(filters)}`, {
      responseType: "blob"
    })
    .then(res => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const today = new Date();
      const stamp = [
        today.getFullYear(),
        String(today.getMonth() + 1).padStart(2, "0"),
        String(today.getDate()).padStart(2, "0")
      ].join("-");
      link.setAttribute("download", `orders_${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
}

export function invalidateOrdersCache() {
  ordersCache.clear();
}
export function sendOrderEmail(orderNumber) {
  return httpStore
    .get(`/orders/send_order_by_email/${orderNumber}`)
    .then(res => {
      return res.data;
    });
}
export function sendOrderToEmail(orderNumber, email, message) {
  return httpStore
    .post(`/orders/send_order_by_email/${orderNumber}`, { email, message })
    .then(res => {
      return res.data;
    });
}
export function verifyOrderProducts(orderNumber) {
  return httpStore.get(`/orders/verify_stock/${orderNumber}`).then(res => {
    return res.data;
  });
}
export function sendOrderSMS(orderNumber, phone, message) {
  return httpStore
    .post(`/orders/send_order_by_sms`, { phone, orderNumber, message })
    .then(res => {
      return res.data;
    });
}
// Orders that contain a product (exact nkr_code), newest first, with that
// product's quantity and amount on each order. Used by the product modal.
export function getOrdersByProduct(sku, page = 1, limitPerPage = 25) {
  return httpStore
    .get(`/orders/by_product`, { params: { sku, page, limitPerPage } })
    .then(res => res.data);
}
export function getOrder(uuid) {
  return httpStore
    .get(`/orders/single/${uuid}`)
    .then(res => {
      return res.data;
    })
    .catch(e => {
      throw e;
    });
}
export function updateOrder(
  orderNumber,
  section,
  newValues,
  recalculatePrices = false
) {
  console.log("updateOrders", newValues);
  return httpStore
    .post(`/orders/update/${orderNumber}`, {
      section,
      order: newValues,
      recalculatePrices
    })
    .then(res => {
      return res.data;
    });
}
export function createShipmentService(orderNumber, newShipment) {
  return httpStore
    .post(`/orders/add_shipment/${orderNumber}`, newShipment)
    .then(res => {
      return res.data;
    });
}
export function addNoteToOrder(orderNumber, message) {
  return httpStore
    .post(`/orders/notes/${orderNumber}`, { message })
    .then(res => {
      return res.data;
    });
}

export function deleteShipmentService(id) {
  return httpStore.delete(`orders/shipment?id=${id}`);
}

export function deletePayment(id, orderNumber) {
  return httpStore.delete(
    `orders/payment?id=${id}&order_number=${orderNumber}`
  );
}

export function addPaymentToOrder(orderNumber, payment) {
  return httpStore.post(`orders/payment/${orderNumber}`, payment).then(data => {
    return data.data;
  });
}

export function recalculatePaidOrders() {
  return httpStore.get(`orders/recalculate_all_orders`).then(data => {
    return data.data;
  });
}

// Rebuilds orders_v2.search_index / list_metadata for every order in one request
// (see Orders::backfill_search_index_menu_get). force=true reprocesses every
// order, not just the ones missing an index — needed after any change to what
// refresh_search_index indexes/caches. Can take a while; the backend removes
// its own execution time limit, but the request itself can still be slow.
export function backfillOrdersSearchIndex() {
  return httpStore
    .get(`orders/backfill_search_index_menu?force=true`)
    .then(data => {
      return data.data;
    });
}

export function completeDraftOrder(orderNumber, split = false) {
  return httpStore
    .get(`orders/complete_draft_order/${orderNumber}?splitIfPossible=${split}`)
    .then(data => {
      return data.data;
    });
}

export function cancelOrder(orderNumber, msg) {
  return httpStore
    .post(`orders/cancel_order/${orderNumber}`, { comment: msg })
    .then(data => {
      return data.data;
    });
}

// Undo a cancellation (superadmin): back to the status before it
export function reactivateOrder(orderNumber, comment) {
  return httpStore
    .post(`orders/reactivate/${orderNumber}`, { comment })
    .then(res => res.data);
}

export function sendToWarehouseAllocation(orderNumber) {
  return httpStore
    .get(`orders/send_order_to_warehouse_allocation/${orderNumber}`)
    .then(data => {
      return data.data;
    });
}

export function loadStatistics(filters) {
  return httpStore.post(`statistics`, { filters: filters }).then(data => {
    return data.data;
  });
}

export function allocateOrderProducts(orderNumber) {
  return httpStore.get(`orders/allocate/${orderNumber}`).then(data => {
    return data.data;
  });
}
export function verifyDropshipProducts(orderNumber) {
  return httpStore.get(`orders/verify_dropship/${orderNumber}`).then(data => {
    return data.data;
  });
}

export function createOrderFromOrderId(orderId) {
  return httpStore
    .get(`orders/create_draft_order_from_another_order/${orderId}`)
    .then(data => {
      return data.data;
    });
}

export function getOrdersQtyByStatus() {
  return httpStore.get("orders/general_status").then(data => {
    // Las llaves son los estados tal cual (orderStatusMap), pero camelize
    // renombra la única con guion bajo.
    return Object.fromEntries(
      Object.entries(data.data).map(([status, qty]) => [
        status === "warehouseAllocation" ? "warehouse_allocation" : status,
        qty
      ])
    );
  });
}

export function splitDropshipOrder(orderNumber) {
  return httpStore
    .get(`orders/split_dropship_order/${orderNumber}`)
    .then(data => {
      return data.data;
    });
}

export function linkShippingCalculation(
  orderNumber,
  calculationId,
  selectedService
) {
  return httpStore
    .post(`orders/linkshippingcalculation`, {
      order_number: orderNumber,
      calculation_id: calculationId,
      selected_service: selectedService
    })
    .then(data => {
      return data.data;
    });
}

export function getShippingCalculation(orderNumber) {
  return httpStore
    .get(`orders/shippingcalculation/${orderNumber}`)
    .then(data => {
      return data.data;
    });
}

// Snapshots de las versiones de una orden (JSON como string, ver CLAUDE.md).
export function getOrderVersionSnapshots(orderNumber) {
  return httpStore
    .get(`/orders/version_snapshots/${orderNumber}`)
    .then(res => res.data);
}
